import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Lead, User } from '../../types';
import { createLead, updateLead, fetchLead, fetchUsersList, checkLeadDuplicate } from '../../services/api';
import { decodeId, encodeId } from '../../utils/hashids';

interface DuplicateLead {
  id: number; name: string; mobile?: string; phone?: string;
  email?: string; status: string; company?: string;
}

interface Props {
  lead?: Lead | null;
  onSaved?: (l: Lead) => void;
  onClose?: () => void;
}

const SOURCES = [
  { value: 'manual', label: 'Manual' },
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'google', label: 'Google Ads' },
  { value: 'indiamart', label: 'IndiaMart' },
  { value: 'justdial', label: 'JustDial' },
  { value: 'referral', label: 'Referral' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const BUSINESS_TYPES = [
  'Pharma Distributor', 'Pharma PCD', 'Pharma Retail', 'Hospital Pharmacy',
  'Institutional', 'Export', 'Other',
];

const SPECIALTIES = ['Cardiac', 'Diabetic', 'Ortho', 'Gastro', 'Neuro', 'Derma', 'Gynec', 'Paeds'];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi NCR', 'Chandigarh',
];

const today = new Date().toISOString().split('T')[0];

const LeadForm: React.FC<Props> = ({ lead: leadProp, onSaved, onClose }) => {
  const history = useHistory();
  const { id: idHash } = useParams<{ id?: string }>();
  const numId          = idHash ? decodeId(idHash) : null;
  const [lead, setLead]     = useState<Lead | null | undefined>(leadProp);
  const [loadingLead, setLoadingLead] = useState(!!idHash && !leadProp);
  const isEdit  = !!lead;
  const [agents, setAgents] = useState<User[]>([]);

  useEffect(() => {
    if (numId && !leadProp) {
      setLoadingLead(true);
      fetchLead(numId)
        .then(setLead)
        .catch(() => history.replace('/leads'))
        .finally(() => setLoadingLead(false));
    }
  }, [idHash, leadProp, history]); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({
    name:             lead?.name              ?? '',
    mobile:           lead?.mobile            ?? '',
    whatsapp_number:  lead?.whatsapp_number   ?? '',
    email:            lead?.email             ?? '',
    city:             lead?.city              ?? '',
    district:         lead?.district          ?? '',
    state:            lead?.state             ?? '',
    business_type:    lead?.business_type     ?? '',
    specialty_interest: lead?.specialty_interest ?? [] as string[],
    moq_min:          lead?.moq_min?.toString()  ?? '',
    moq_max:          lead?.moq_max?.toString()  ?? '',
    coverage_districts: lead?.coverage_districts ?? '',
    current_supplier:   lead?.current_supplier   ?? '',
    mr_count:           lead?.mr_count?.toString() ?? '0',
    years_in_business:  lead?.years_in_business?.toString() ?? '0',
    erp_software:       lead?.erp_software      ?? '',
    source:             lead?.source             ?? 'manual',
    campaign:           lead?.campaign           ?? '',
    first_touch_date:   lead?.first_touch_date   ?? today,
    assigned_to:        lead?.assigned_to?.toString() ?? '',
    expected_order_value: lead?.expected_order_value?.toString() ?? '',
    expected_close_date:  lead?.expected_close_date ?? '',
  });

  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateLead[]>([]);

  const runDuplicateCheck = async () => {
    const mobile = form.mobile.trim();
    const email  = form.email.trim();
    if (!mobile && !email) { setDuplicates([]); return; }
    try {
      const res = await checkLeadDuplicate({
        mobile: mobile || undefined,
        email:  email  || undefined,
        exclude_id: isEdit && lead ? lead.id : undefined,
      });
      setDuplicates(res.duplicates ?? []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    fetchUsersList().then(setAgents).catch(() => {});
  }, []);

  useEffect(() => {
    if (!lead) return;
    setForm({
      name:             lead.name              ?? '',
      mobile:           lead.mobile            ?? '',
      whatsapp_number:  lead.whatsapp_number   ?? '',
      email:            lead.email             ?? '',
      city:             lead.city              ?? '',
      district:         lead.district          ?? '',
      state:            lead.state             ?? '',
      business_type:    lead.business_type     ?? '',
      specialty_interest: lead.specialty_interest ?? [],
      moq_min:          lead.moq_min?.toString()  ?? '',
      moq_max:          lead.moq_max?.toString()  ?? '',
      coverage_districts: lead.coverage_districts ?? '',
      current_supplier:   lead.current_supplier   ?? '',
      mr_count:           lead.mr_count?.toString() ?? '0',
      years_in_business:  lead.years_in_business?.toString() ?? '0',
      erp_software:       lead.erp_software      ?? '',
      source:             lead.source             ?? 'manual',
      campaign:           lead.campaign           ?? '',
      first_touch_date:   lead.first_touch_date   ?? today,
      assigned_to:        lead.assigned_to?.toString() ?? '',
      expected_order_value: lead.expected_order_value?.toString() ?? '',
      expected_close_date:  lead.expected_close_date ?? '',
    });
  }, [lead]);

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const toggleSpecialty = (s: string) => {
    setForm(f => ({
      ...f,
      specialty_interest: f.specialty_interest.includes(s)
        ? f.specialty_interest.filter(x => x !== s)
        : [...f.specialty_interest, s],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setErrors({});
    try {
      const payload = {
        ...form,
        moq_min: form.moq_min ? parseFloat(form.moq_min) : undefined,
        moq_max: form.moq_max ? parseFloat(form.moq_max) : undefined,
        mr_count: parseInt(form.mr_count) || 0,
        years_in_business: parseInt(form.years_in_business) || 0,
        expected_order_value: form.expected_order_value ? parseFloat(form.expected_order_value) : undefined,
        assigned_to: form.assigned_to ? parseInt(form.assigned_to) : undefined,
      };

      const saved = isEdit && lead
        ? await updateLead(lead.id, payload)
        : await createLead(payload);

      if (onSaved) onSaved(saved);
      else history.push('/leads');
    } catch (err: any) {
      if (err?.response?.data?.errors) {
        const mapped: Record<string, string> = {};
        Object.entries(err.response.data.errors).forEach(([k, v]) => {
          mapped[k] = (v as string[])[0];
        });
        setErrors(mapped);
      }
    } finally { setLoading(false); }
  };

  const handleDiscard = () => {
    if (onClose) onClose();
    else history.push('/leads');
  };

  if (loadingLead) return <div className="spinner">Loading…</div>;

  return (
    <div className="lead-form-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button className="breadcrumb-back" onClick={handleDiscard}>
          Leads / {isEdit ? lead?.name : 'New Lead'}
        </button>
      </div>

      <div className="lead-form-head">
        <div>
          <h1 className="page-title">{isEdit ? 'Edit Lead' : 'New Lead'}</h1>
          <p className="page-meta">* Required fields</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={handleDiscard}>Discard</button>
          <button type="submit" form="lead-form" className="btn btn-primary" disabled={loading}>
            {loading ? <><span className="btn-spinner" />Saving…</> : isEdit ? 'Save Changes' : 'Save Lead'}
          </button>
        </div>
      </div>

      <form id="lead-form" onSubmit={handleSubmit} className="lead-form-body">

        {/* ── Duplicate warning (non-blocking) ── */}
        {duplicates.length > 0 && (
          <div style={{
            background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8,
            padding: '12px 16px', marginBottom: 16,
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#854d0e', marginBottom: 6 }}>
              ⚠ Possible duplicate{duplicates.length > 1 ? 's' : ''} — {duplicates.length} existing lead{duplicates.length > 1 ? 's' : ''} with this mobile/email
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {duplicates.map(d => (
                <a key={d.id}
                  href={`/leads/${encodeId(d.id)}`}
                  target="_blank" rel="noreferrer"
                  style={{ fontSize: 13, color: '#7c3aed', textDecoration: 'none' }}>
                  {d.name}{d.company ? ` · ${d.company}` : ''} · {d.mobile || d.phone || d.email} · <span style={{ textTransform: 'capitalize' }}>{d.status.replace(/_/g, ' ')}</span> ↗
                </a>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
              You can still save — this is just a heads-up.
            </div>
          </div>
        )}

        {/* ── Contact Information ── */}
        <div className="lead-form-section">
          <h3 className="lead-section-title">Contact Information</h3>
          <div className="lead-form-grid">
            <div className="lead-form-group">
              <label className="lead-form-label">Full Name <span className="required">*</span></label>
              <input className={`lead-form-input${errors.name ? ' input-error' : ''}`}
                name="name" value={form.name} onChange={handle} placeholder="Ramesh Kumar" required />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Mobile Number <span className="required">*</span></label>
              <input className={`lead-form-input${errors.mobile ? ' input-error' : ''}`}
                name="mobile" value={form.mobile} onChange={handle} onBlur={runDuplicateCheck} placeholder="98765 43210" required />
              {errors.mobile && <span className="field-error">{errors.mobile}</span>}
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">WhatsApp Number</label>
              <input className="lead-form-input" name="whatsapp_number" value={form.whatsapp_number}
                onChange={handle} placeholder="Same as mobile" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Email</label>
              <input className="lead-form-input" type="email" name="email" value={form.email}
                onChange={handle} onBlur={runDuplicateCheck} placeholder="owner@business.com" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">City</label>
              <input className="lead-form-input" name="city" value={form.city} onChange={handle} placeholder="Patna" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">District</label>
              <input className="lead-form-input" name="district" value={form.district} onChange={handle} placeholder="Patna" />
            </div>
            <div className="lead-form-group lead-form-full">
              <label className="lead-form-label">State</label>
              <select className="lead-form-input" name="state" value={form.state} onChange={handle}>
                <option value="">Select state…</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Business Profile ── */}
        <div className="lead-form-section">
          <h3 className="lead-section-title">Business Profile</h3>
          <div className="lead-form-grid">
            <div className="lead-form-group">
              <label className="lead-form-label">Business Type</label>
              <select className="lead-form-input" name="business_type" value={form.business_type} onChange={handle}>
                <option value="">Select…</option>
                {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="lead-form-group lead-form-full">
              <label className="lead-form-label">Speciality Interest</label>
              <div className="specialty-chips">
                {SPECIALTIES.map(s => (
                  <button key={s} type="button"
                    className={`specialty-chip${form.specialty_interest.includes(s) ? ' specialty-chip-active' : ''}`}
                    onClick={() => toggleSpecialty(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">MOQ Estimate (₹/mo)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="lead-form-input" name="moq_min" type="number" value={form.moq_min}
                  onChange={handle} placeholder="80,000" />
                <span style={{ alignSelf: 'center', color: 'var(--text-muted)' }}>–</span>
                <input className="lead-form-input" name="moq_max" type="number" value={form.moq_max}
                  onChange={handle} placeholder="1,20,000" />
              </div>
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Coverage Districts</label>
              <input className="lead-form-input" name="coverage_districts" value={form.coverage_districts}
                onChange={handle} placeholder="Patna, Gaya, Nalanda" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Current Supplier</label>
              <input className="lead-form-input" name="current_supplier" value={form.current_supplier}
                onChange={handle} placeholder="Sun Pharma, Cipla…" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">MR Count</label>
              <input className="lead-form-input" type="number" name="mr_count" value={form.mr_count}
                onChange={handle} min="0" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Years in Business</label>
              <input className="lead-form-input" type="number" name="years_in_business" value={form.years_in_business}
                onChange={handle} min="0" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">ERP Software</label>
              <input className="lead-form-input" name="erp_software" value={form.erp_software}
                onChange={handle} placeholder="None / Tally / SAP" />
            </div>
          </div>
        </div>

        {/* ── Lead Origin ── */}
        <div className="lead-form-section">
          <h3 className="lead-section-title">Lead Origin</h3>
          <div className="lead-form-grid">
            <div className="lead-form-group">
              <label className="lead-form-label">Source <span className="required">*</span></label>
              <select className="lead-form-input" name="source" value={form.source} onChange={handle}>
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Campaign</label>
              <input className="lead-form-input" name="campaign" value={form.campaign}
                onChange={handle} placeholder="Q1-Cardiac-2026" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">First Touch Date</label>
              <input className="lead-form-input" type="date" name="first_touch_date"
                value={form.first_touch_date} onChange={handle} />
            </div>
          </div>
        </div>

        {/* ── Assignment ── */}
        <div className="lead-form-section">
          <h3 className="lead-section-title">Assignment</h3>
          <div className="lead-form-grid">
            <div className="lead-form-group">
              <label className="lead-form-label">Assigned To</label>
              <select className="lead-form-input" name="assigned_to" value={form.assigned_to} onChange={handle}>
                <option value="">Unassigned</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Expected Order Value (₹/mo)</label>
              <input className="lead-form-input" type="number" name="expected_order_value"
                value={form.expected_order_value} onChange={handle} placeholder="110000" />
            </div>
            <div className="lead-form-group">
              <label className="lead-form-label">Expected Close Date</label>
              <input className="lead-form-input" type="date" name="expected_close_date"
                value={form.expected_close_date} onChange={handle} />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="lead-form-footer">
          <p className="page-meta">* Required fields</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={handleDiscard}>Discard</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="btn-spinner" />Saving…</> : isEdit ? 'Save Changes' : 'Save Lead'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
